import React, { useEffect, useState } from 'react';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { UserIcon, MusicIcon, PencilIcon, SaveIcon, MailIcon, PhoneIcon, LoaderIcon } from 'lucide-react';
import { StudentProfilePhotoUpload } from '../students/StudentProfilePhotoUpload';
export const StudentMyProfile = () => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingBasic, setIsSavingBasic] = useState(false);
  const [isSavingMusic, setIsSavingMusic] = useState(false);
  // Data States
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [basicInfo, setBasicInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [musicInfo, setMusicInfo] = useState({
    instrument: 'Piano',
    skillLevel: 'Beginner'
  });
  // Edit States
  const [isEditingBasic, setIsEditingBasic] = useState(false);
  const [isEditingMusic, setIsEditingMusic] = useState(false);
  // Temp Edit States (for canceling)
  const [tempBasicInfo, setTempBasicInfo] = useState({
    ...basicInfo
  });
  const [tempMusicInfo, setTempMusicInfo] = useState({
    ...musicInfo
  });
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          const fetchedBasicInfo = {
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || currentUser.email || '',
            phone: data.phone || ''
          };
          const fetchedMusicInfo = {
            instrument: data.instrument || 'Piano',
            skillLevel: data.skillLevel || 'Beginner'
          };
          setProfileImageUrl(data.profileImageUrl || null);
          setBasicInfo(fetchedBasicInfo);
          setTempBasicInfo(fetchedBasicInfo);
          setMusicInfo(fetchedMusicInfo);
          setTempMusicInfo(fetchedMusicInfo);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, [currentUser]);
  // Handlers for Basic Info
  const handleEditBasic = () => {
    setTempBasicInfo({
      ...basicInfo
    });
    setIsEditingBasic(true);
  };
  const handleCancelBasic = () => {
    setIsEditingBasic(false);
  };
  const handleSaveBasic = async () => {
    if (!currentUser?.uid) return;
    if (!tempBasicInfo.firstName.trim() || !tempBasicInfo.lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }
    setIsSavingBasic(true);
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        firstName: tempBasicInfo.firstName.trim(),
        lastName: tempBasicInfo.lastName.trim(),
        phone: tempBasicInfo.phone.trim(),
        updatedAt: new Date()
      });
      setBasicInfo({
        ...tempBasicInfo
      });
      setIsEditingBasic(false);
      toast.success('Basic information updated');
    } catch (error) {
      console.error('Error updating basic info:', error);
      toast.error('Failed to update basic information');
    } finally {
      setIsSavingBasic(false);
    }
  };
  // Handlers for Music Info
  const handleEditMusic = () => {
    setTempMusicInfo({
      ...musicInfo
    });
    setIsEditingMusic(true);
  };
  const handleCancelMusic = () => {
    setIsEditingMusic(false);
  };
  const handleSaveMusic = async () => {
    if (!currentUser?.uid) return;
    setIsSavingMusic(true);
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        instrument: tempMusicInfo.instrument,
        skillLevel: tempMusicInfo.skillLevel,
        updatedAt: new Date()
      });
      setMusicInfo({
        ...tempMusicInfo
      });
      setIsEditingMusic(false);
      toast.success('Music information updated');
    } catch (error) {
      console.error('Error updating music info:', error);
      toast.error('Failed to update music information');
    } finally {
      setIsSavingMusic(false);
    }
  };
  // Common styles based on theme
  const cardClass = `rounded-xl p-6 shadow-sm border transition-all ${isDark ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`;
  const labelClass = `block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`;
  const valueClass = `text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`;
  const inputClass = `w-full px-4 py-2.5 rounded-lg border transition-colors focus:ring-2 focus:ring-green-500/50 focus:border-green-500 outline-none ${isDark ? 'bg-navy-900 border-navy-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}`;
  const readOnlyInputClass = `w-full px-4 py-2.5 rounded-lg border cursor-not-allowed ${isDark ? 'bg-navy-900/50 border-navy-700 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}`;
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-7471">
        <LoaderIcon className={`w-8 h-8 animate-spin ${isDark ? 'text-green-400' : 'text-green-500'}`} data-id="element-7472" />
      </div>;
  }
  return <div className="min-h-screen p-6 md:p-8" data-id="element-7473">
      <div className="max-w-[900px] mx-auto space-y-8" data-id="element-7474">
        {/* Page Header */}
        <div data-id="element-7475">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7476">
            My Profile
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7477">
            Manage your personal details and music preferences.
          </p>
        </div>

        {/* Profile Photo Section */}
        <div className={cardClass} data-id="element-7478">
          <StudentProfilePhotoUpload uid={currentUser?.uid || ''} currentPhotoUrl={profileImageUrl} firstName={basicInfo.firstName} lastName={basicInfo.lastName} isDark={isDark} onPhotoUpdated={setProfileImageUrl} data-id="element-7479" />
        </div>

        {/* Section 1: Basic Information */}
        <div className={cardClass} data-id="element-7480">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-navy-700" data-id="element-7481">
            <div className="flex items-center gap-3" data-id="element-7482">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-900 text-green-400' : 'bg-green-50 text-green-600'}`} data-id="element-7483">
                <UserIcon className="w-5 h-5" data-id="element-7484" />
              </div>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7485">
                Basic Information
              </h2>
            </div>
            {!isEditingBasic && <button onClick={handleEditBasic} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-7486">
                <PencilIcon className="w-4 h-4" data-id="element-7487" />
                Edit
              </button>}
          </div>

          {isEditingBasic ? <div className="space-y-5" data-id="element-7488">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7489">
                <div data-id="element-7490">
                  <label className={labelClass} data-id="element-7491">First Name</label>
                  <input type="text" value={tempBasicInfo.firstName} onChange={e => setTempBasicInfo({
                ...tempBasicInfo,
                firstName: e.target.value
              })} className={inputClass} placeholder="First name" data-id="element-7492" />
                </div>
                <div data-id="element-7493">
                  <label className={labelClass} data-id="element-7494">Last Name</label>
                  <input type="text" value={tempBasicInfo.lastName} onChange={e => setTempBasicInfo({
                ...tempBasicInfo,
                lastName: e.target.value
              })} className={inputClass} placeholder="Last name" data-id="element-7495" />
                </div>
                <div data-id="element-7496">
                  <label className={labelClass} data-id="element-7497">Email Address</label>
                  <div className="relative" data-id="element-7498">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7499">
                      <MailIcon className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7500" />
                    </div>
                    <input type="email" value={tempBasicInfo.email} readOnly className={`${readOnlyInputClass} pl-10`} data-id="element-7501" />
                  </div>
                  <p className={`mt-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-7502">
                    Email cannot be changed directly.
                  </p>
                </div>
                <div data-id="element-7503">
                  <label className={labelClass} data-id="element-7504">Phone Number (Optional)</label>
                  <div className="relative" data-id="element-7505">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7506">
                      <PhoneIcon className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7507" />
                    </div>
                    <input type="tel" value={tempBasicInfo.phone} onChange={e => setTempBasicInfo({
                  ...tempBasicInfo,
                  phone: e.target.value
                })} className={`${inputClass} pl-10`} placeholder="(555) 123-4567" data-id="element-7508" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 mt-6 border-t border-gray-200 dark:border-navy-700" data-id="element-7509">
                <button onClick={handleCancelBasic} disabled={isSavingBasic} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} disabled:opacity-50`} data-id="element-7510">
                  Cancel
                </button>
                <button onClick={handleSaveBasic} disabled={isSavingBasic} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-70" data-id="element-7511">
                  {isSavingBasic ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-7512" /> : <SaveIcon className="w-4 h-4" data-id="element-7513" />}
                  {isSavingBasic ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8" data-id="element-7514">
              <div data-id="element-7515">
                <span className={labelClass} data-id="element-7516">Full Name</span>
                <span className={valueClass} data-id="element-7517">
                  {basicInfo.firstName} {basicInfo.lastName}
                </span>
              </div>
              <div data-id="element-7518">
                <span className={labelClass} data-id="element-7519">Email Address</span>
                <span className={valueClass} data-id="element-7520">{basicInfo.email}</span>
              </div>
              <div data-id="element-7521">
                <span className={labelClass} data-id="element-7522">Phone Number</span>
                <span className={valueClass} data-id="element-7523">
                  {basicInfo.phone || 'Not provided'}
                </span>
              </div>
            </div>}
        </div>

        {/* Section 2: Music Information */}
        <div className={cardClass} data-id="element-7524">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-navy-700" data-id="element-7525">
            <div className="flex items-center gap-3" data-id="element-7526">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-900 text-blue-400' : 'bg-blue-50 text-blue-600'}`} data-id="element-7527">
                <MusicIcon className="w-5 h-5" data-id="element-7528" />
              </div>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7529">
                Music Information
              </h2>
            </div>
            {!isEditingMusic && <button onClick={handleEditMusic} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-7530">
                <PencilIcon className="w-4 h-4" data-id="element-7531" />
                Edit
              </button>}
          </div>

          {isEditingMusic ? <div className="space-y-5" data-id="element-7532">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7533">
                <div data-id="element-7534">
                  <label className={labelClass} data-id="element-7535">Primary Instrument</label>
                  <select value={tempMusicInfo.instrument} onChange={e => setTempMusicInfo({
                ...tempMusicInfo,
                instrument: e.target.value
              })} className={inputClass} data-id="element-7536">
                    <option value="Piano" data-id="element-7537">Piano</option>
                    <option value="Guitar" data-id="element-7538">Guitar</option>
                    <option value="Violin" data-id="element-7539">Violin</option>
                    <option value="Voice" data-id="element-7540">Voice</option>
                    <option value="Drums" data-id="element-7541">Drums</option>
                    <option value="Bass" data-id="element-7542">Bass</option>
                    <option value="Other" data-id="element-7543">Other</option>
                  </select>
                </div>
                <div data-id="element-7544">
                  <label className={labelClass} data-id="element-7545">Skill Level</label>
                  <select value={tempMusicInfo.skillLevel} onChange={e => setTempMusicInfo({
                ...tempMusicInfo,
                skillLevel: e.target.value
              })} className={inputClass} data-id="element-7546">
                    <option value="Beginner" data-id="element-7547">Beginner</option>
                    <option value="Intermediate" data-id="element-7548">Intermediate</option>
                    <option value="Advanced" data-id="element-7549">Advanced</option>
                    <option value="Professional" data-id="element-7550">Professional</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 mt-6 border-t border-gray-200 dark:border-navy-700" data-id="element-7551">
                <button onClick={handleCancelMusic} disabled={isSavingMusic} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} disabled:opacity-50`} data-id="element-7552">
                  Cancel
                </button>
                <button onClick={handleSaveMusic} disabled={isSavingMusic} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-70" data-id="element-7553">
                  {isSavingMusic ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-7554" /> : <SaveIcon className="w-4 h-4" data-id="element-7555" />}
                  {isSavingMusic ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8" data-id="element-7556">
              <div data-id="element-7557">
                <span className={labelClass} data-id="element-7558">Primary Instrument</span>
                <span className={valueClass} data-id="element-7559">{musicInfo.instrument}</span>
              </div>
              <div data-id="element-7560">
                <span className={labelClass} data-id="element-7561">Skill Level</span>
                <span className={valueClass} data-id="element-7562">{musicInfo.skillLevel}</span>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};